import React, { useEffect, useState } from 'react';
import {
  AnonymousRoute,
  AuthChangeRedirector,
  AuthenticatedRoute,
  useConfig,
} from './allauth/auth';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import RequestLoginCode from './allauth/account/RequestLoginCode';
import ConfirmLoginCode from './allauth/account/ConfirmLoginCode';
import Logout from './allauth/account/Logout';
import Signup from './allauth/account/Signup';
import ProviderSignup from './allauth/socialaccount/ProviderSignup';
import ProviderCallback from './allauth/socialaccount/ProviderCallback';
import ChangeEmail from './allauth/account/ChangeEmail';
import ManageProviders from './allauth/socialaccount/ManageProviders';
import VerifyEmail, { loader as verifyEmailLoader } from './allauth/account/VerifyEmail';
import VerifyEmailByCode from './allauth/account/VerifyEmailByCode';
import VerificationEmailSent from './allauth/account/VerificationEmailSent';
import RequestPasswordReset from './allauth/account/RequestPasswordReset';
import ConfirmPasswordResetCode from './allauth/account/ConfirmPasswordResetCode';
import ChangePassword from './allauth/account/ChangePassword';
import MFAOverview, { loader as mfaOverviewLoader } from './allauth/mfa/MFAOverview';
import ActivateTOTP, { loader as activateTOTPLoader } from './allauth/mfa/ActivateTOTP';
import DeactivateTOTP from './allauth/mfa/DeactivateTOTP';
import RecoveryCodes, { loader as recoveryCodesLoader } from './allauth/mfa/RecoveryCodes';
import AddWebAuthn from './allauth/mfa/AddWebAuthn';
import SignupByPasskey from './allauth/mfa/SignupByPasskey';
import ReauthenticateWebAuthn from './allauth/mfa/ReauthenticateWebAuthn';
import ListWebAuthn, { loader as listWebAuthnLoader } from './allauth/mfa/ListWebAuthn';
import GenerateRecoveryCodes, {
  loader as generateRecoveryCodesLoader,
} from './allauth/mfa/GenerateRecoveryCodes';
import {
  ResetPasswordByCode,
  ResetPasswordByLink,
  resetPasswordByLinkLoader,
} from './allauth/account/ResetPassword';
import AuthenticateTOTP from './allauth/mfa/AuthenticateTOTP';
import AuthenticateRecoveryCodes from './allauth/mfa/AuthenticateRecoveryCodes';
import AuthenticateWebAuthn from './allauth/mfa/AuthenticateWebAuthn';
import ReauthenticateRecoveryCodes from './allauth/mfa/ReauthenticateRecoveryCodes';
import ReauthenticateTOTP from './allauth/mfa/ReauthenticateTOTP';
import CreateSignupPasskey from './allauth/mfa/CreateSignupPasskey';
import Trust from './allauth/mfa/Trust';
import Reauthenticate from './allauth/account/Reauthenticate';
import Sessions from './allauth/usersessions/Sessions';
import Root from './allauth/Root';
import TrackerMap3 from './components/screens/TrackerMap3';
import Landing from './components/screens/Landing';
import CampaignSelect from './components/screens/CampaignSelectScreen';
import Dashboard from './components/screens/Dashboard';
import Home from './components/screens/Home';
import SitesMap from './components/screens/SitesMap';
import Sites from './components/screens/Sites';
import Canvassers from './components/screens/Canvassers';
import Admin from './components/screens/Admin';
import CreateSite from './components/screens/CreateSite';
import MapReports from './components/screens/MapReports';
import TimelineSpeedMap from './components/screens/TimelineSpeedMap';
import Scripts from './components/screens/Scripts';
import ScriptResponses from './components/theme/dashboard/charts/ScriptResponses';
import ScriptEdit from './components/screens/ScriptEdit';
import SiteCanvasses from './components/screens/SiteCanvasses';
import Surveys from './components/screens/Surveys';
import SiteRoutesScreen from './components/screens/SiteRoutesScreen';
import RouteEdit from './components/screens/RouteEdit';
import SimpleSiteRouteEdit from './components/screens/SimpeSiteRouteEdit';
import Schedule from './components/screens/Schedule';
import DoorCanvassScreen from './components/screens/DoorCanvassScreen';
import DoorSchedule from './components/screens/DoorSchedule';
import TurfCutter from './components/screens/TurfCutter';
import UberRidesScreen from './components/screens/UberRidesScreen';
import Profile from './components/screens/Profile';
import SiteCrewSheet from './components/screens/SiteCrewSheet';
import DoorCrewSheet from './components/screens/DoorCrewSheet';
import OverviewScreen from './components/screens/OverviewScreen';
import DoorCanvassStatusReport from './components/screens/DoorCanvassStatusReport';
import DoorsUpload from './components/screens/DoorsUpload';
import { AdminRoute, AuthenticatedNavRoute } from './allauth/auth/routing';
import LoginScreen from './components/screens/LoginScreen';
import NavBaseTemplate from './components/templates/NavBaseTemplate';
import '../node_modules/milligram/dist/milligram.css';
import './App.scss';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import Startup from './components/screens/Startup';
import AllowUberRoute from "./components/uber/AllowUberRoute";

function createRouter(config) {
  const allRoutes = [
    {
      path: '/',
      element: (
        <AuthChangeRedirector>
          <Root />
        </AuthChangeRedirector>
      ),
      children: [
        {
          path: '/',
          element: <Landing />,
        },
        {
          path: '/startup',
          element: (
            <AuthenticatedRoute>
              <Startup />
            </AuthenticatedRoute>
          ),
        },
        {
          path: '/login',
          element: (
            <AnonymousRoute>
              <LoginScreen />
            </AnonymousRoute>
          ),
        },
        {
          path: '/account/login/code',
          element: (
            <AnonymousRoute>
              <RequestLoginCode />
            </AnonymousRoute>
          ),
        },
        {
          path: '/account/login/code/confirm',
          element: (
            <AnonymousRoute>
              <ConfirmLoginCode />
            </AnonymousRoute>
          ),
        },
        {
          path: '/account/email',
          element: (
            <AuthenticatedNavRoute>
              <ChangeEmail />
            </AuthenticatedNavRoute>
          ),
        },
        {
          path: '/account/logout',
          element: (
            <AuthenticatedNavRoute>
              <Logout />
            </AuthenticatedNavRoute>
          ),
        },
        {
          path: '/account/provider/callback',
          element: <ProviderCallback />,
        },
        {
          path: '/account/provider/signup',
          element: (
            <AnonymousRoute>
              <ProviderSignup />
            </AnonymousRoute>
          ),
        },
        {
          path: '/account/providers',
          element: (
            <AuthenticatedNavRoute>
              <ManageProviders />
            </AuthenticatedNavRoute>
          ),
        },
        {
          path: '/account/signup',
          element: (
            <AnonymousRoute>
              <Signup />
            </AnonymousRoute>
          ),
        },
        {
          path: '/account/signup/passkey',
          element: (
            <AnonymousRoute>
              <SignupByPasskey />
            </AnonymousRoute>
          ),
        },
        {
          path: '/account/signup/passkey/create',
          element: (
            <AnonymousRoute>
              <CreateSignupPasskey />
            </AnonymousRoute>
          ),
        },
        {
          path: '/account/verify-email',
          element: config.data.account.email_verification_by_code_enabled ? (
            <AuthenticatedNavRoute>
              <VerifyEmailByCode />
            </AuthenticatedNavRoute>
          ) : (
            <VerificationEmailSent />
          ),
        },
        {
          path: '/account/verify-email/:key',
          element: <VerifyEmail />,
          loader: verifyEmailLoader,
        },
        {
          path: '/account/password/reset',
          element: (
            <AnonymousRoute>
              <RequestPasswordReset />
            </AnonymousRoute>
          ),
        },
        {
          path: '/account/password/reset/confirm',
          element: (
            <AnonymousRoute>
              <ConfirmPasswordResetCode />
            </AnonymousRoute>
          ),
        },
        {
          path: '/account/password/reset/complete',
          element: (
            <AnonymousRoute>
              <ResetPasswordByCode />
            </AnonymousRoute>
          ),
        },
        {
          path: '/account/password/reset/key/:key',
          element: (
            <AnonymousRoute>
              <ResetPasswordByLink />
            </AnonymousRoute>
          ),
          loader: resetPasswordByLinkLoader,
        },
        {
          path: '/account/password/change',
          element: (
            <AuthenticatedNavRoute>
              <ChangePassword />
            </AuthenticatedNavRoute>
          ),
        },
        {
          path: '/account/2fa',
          element: (
            <AuthenticatedNavRoute>
              <MFAOverview />
            </AuthenticatedNavRoute>
          ),
          loader: mfaOverviewLoader,
        },
        {
          path: '/account/reauthenticate',
          element: (
            <AuthenticatedNavRoute>
              <Reauthenticate />
            </AuthenticatedNavRoute>
          ),
        },
        {
          path: '/account/reauthenticate/totp',
          element: (
            <AuthenticatedNavRoute>
              <ReauthenticateTOTP />
            </AuthenticatedNavRoute>
          ),
        },
        {
          path: '/account/reauthenticate/recovery-codes',
          element: (
            <AuthenticatedNavRoute>
              <ReauthenticateRecoveryCodes />
            </AuthenticatedNavRoute>
          ),
        },
        {
          path: '/account/reauthenticate/webauthn',
          element: (
            <AuthenticatedNavRoute>
              <ReauthenticateWebAuthn />
            </AuthenticatedNavRoute>
          ),
        },
        {
          path: '/account/authenticate/totp',
          element: (
            <AnonymousRoute>
              <AuthenticateTOTP />
            </AnonymousRoute>
          ),
        },
        {
          path: '/account/2fa/trust',
          element: (
            <AnonymousRoute>
              <Trust />
            </AnonymousRoute>
          ),
        },
        {
          path: '/account/authenticate/recovery-codes',
          element: (
            <AnonymousRoute>
              <AuthenticateRecoveryCodes />
            </AnonymousRoute>
          ),
        },
        {
          path: '/account/authenticate/webauthn',
          element: (
            <AnonymousRoute>
              <AuthenticateWebAuthn />
            </AnonymousRoute>
          ),
        },
        {
          path: '/account/2fa/totp/activate',
          element: (
            <AuthenticatedNavRoute>
              <ActivateTOTP />
            </AuthenticatedNavRoute>
          ),
          loader: activateTOTPLoader,
        },
        {
          path: '/account/2fa/totp/deactivate',
          element: (
            <AuthenticatedNavRoute>
              <DeactivateTOTP />
            </AuthenticatedNavRoute>
          ),
        },
        {
          path: '/account/2fa/recovery-codes',
          element: (
            <AuthenticatedNavRoute>
              <RecoveryCodes />
            </AuthenticatedNavRoute>
          ),
          loader: recoveryCodesLoader,
        },
        {
          path: '/account/2fa/recovery-codes/generate',
          element: (
            <AuthenticatedNavRoute>
              <GenerateRecoveryCodes />
            </AuthenticatedNavRoute>
          ),
          loader: generateRecoveryCodesLoader,
        },
        {
          path: '/account/2fa/webauthn',
          element: (
            <AuthenticatedNavRoute>
              <ListWebAuthn />
            </AuthenticatedNavRoute>
          ),
          loader: listWebAuthnLoader,
        },
        {
          path: '/account/2fa/webauthn/add',
          element: (
            <AuthenticatedNavRoute>
              <AddWebAuthn />
            </AuthenticatedNavRoute>
          ),
        },
        {
          path: '/account/sessions',
          element: (
            <AuthenticatedNavRoute>
              <Sessions />
            </AuthenticatedNavRoute>
          ),
        },
      ],
    },
  ];

  allRoutes[0].children.push({
    path: `/map3`,
    element: (
      <AuthenticatedRoute>
        <TrackerMap3 />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/map',
    element: (
      <AuthenticatedRoute>
        <TrackerMap3 />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/site',
    element: (
      <AuthenticatedRoute>
        <SitesMap />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/site-list',
    element: (
      <AuthenticatedRoute>
        <Sites />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/canvasser',
    element: (
      <AuthenticatedRoute>
        <Canvassers />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/admin/:section?',
    element: (
      <AdminRoute>
        <Admin />
      </AdminRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/create-site',
    element: (
      <AuthenticatedRoute>
        <CreateSite />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/map-report',
    element: (
      <AuthenticatedRoute>
        <MapReports />
      </AuthenticatedRoute>
    ),
  }); // OBSOLETE
  //    allRoutes[0].children.push({path: "/map-timeline", element: <AuthenticatedRoute><TimelineSpeedMap /></AuthenticatedRoute>}); // OBSOLETE
  allRoutes[0].children.push({
    path: '/script',
    element: (
      <AuthenticatedRoute>
        <Scripts />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/dash/script-responses',
    element: (
      <AuthenticatedRoute>
        <ScriptResponses />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/script/:script_id/edit',
    element: (
      <AuthenticatedRoute>
        <ScriptEdit />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/select-campaign',
    element: (
      <AuthenticatedRoute>
        <CampaignSelect />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/sitecanvass',
    element: (
      <AuthenticatedRoute>
        <SiteCanvasses />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/survey',
    element: (
      <AuthenticatedRoute>
        <Surveys />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/sitecanvass/:sitecanvass_id',
    element: (
      <AuthenticatedRoute>
        <SiteRoutesScreen />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/sitecanvass/:sitecanvass_id/edit',
    element: (
      <AuthenticatedRoute>
        <RouteEdit />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/sitecanvass/:sitecanvass_id/quickedit',
    element: (
      <AuthenticatedRoute>
        <SimpleSiteRouteEdit />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/sitecanvass/:sitecanvass_id/schedule',
    element: (
      <AuthenticatedRoute>
        <Schedule />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/doorcanvass',
    element: (
      <AuthenticatedRoute>
        <DoorCanvassScreen />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/doorcanvass/:doorcanvass_id/schedule',
    element: (
      <AuthenticatedRoute>
        <DoorSchedule />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/turfcutter/:doorcanvass_pass_id',
    element: (
      <AuthenticatedRoute>
        <TurfCutter />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/uber-rides',
    element: (
        <AuthenticatedRoute>
          <AllowUberRoute>
            <UberRidesScreen />
          </AllowUberRoute>
        </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/profile',
    element: (
      <AuthenticatedRoute>
        <NavBaseTemplate>
          <Profile />
        </NavBaseTemplate>
      </AuthenticatedRoute>
    ),
  });

  
  allRoutes[0].children.push({
    path: '/crewsheet/:shift',
    element: (
      <AuthenticatedRoute>
        <SiteCrewSheet />
       </AuthenticatedRoute>
    ),
  });   

  allRoutes[0].children.push({
    path: '/site-crewsheet/:shift',
    element: (
      <AuthenticatedRoute>
        <SiteCrewSheet />
      </AuthenticatedRoute>
    ),
  });

  allRoutes[0].children.push({
    path: '/door-crewsheet/:shift',
    element: (
      <AuthenticatedRoute>
        <DoorCrewSheet />
      </AuthenticatedRoute>
    ),
  });

  allRoutes[0].children.push({
    path: '/dash',
    element: (
      <AuthenticatedRoute>
        <Dashboard />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/dash/old',
    element: (
      <AuthenticatedRoute>
        <Home />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/dash/schedule-overview',
    element: (
      <AuthenticatedRoute>
        <OverviewScreen />
      </AuthenticatedRoute>
    ),
  });
  allRoutes[0].children.push({
    path: '/dash/door-statuses',
    element: (
      <AuthenticatedRoute>
        <DoorCanvassStatusReport />
      </AuthenticatedRoute>
    ),
  });

  allRoutes[0].children.push({
    path: '/doors-upload',
    element: (
      <AuthenticatedRoute>
        <DoorsUpload />
      </AuthenticatedRoute>
    ),
  });

  return createBrowserRouter(allRoutes);
}

export default function Router() {
  const [router, setRouter] = useState(null);

  const config = useConfig();
  useEffect(() => {
    setRouter(createRouter(config));
  }, [config]);

  return router ? <RouterProvider router={router} /> : null;
}
